import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaAward, FaUsers, FaTools, FaShieldAlt, FaChartLine, FaHandshake } from 'react-icons/fa';
import BaseModal from './BaseModal';

/**
 * About Us Modal Component
 * Displays detailed company information
 */
const AboutModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  const highlights = [
    { icon: FaAward, key: 'aboutModal.highlight1' },
    { icon: FaUsers, key: 'aboutModal.highlight2' },
    { icon: FaTools, key: 'aboutModal.highlight3' },
    { icon: FaShieldAlt, key: 'aboutModal.highlight4' },
    { icon: FaChartLine, key: 'aboutModal.highlight5' },
    { icon: FaHandshake, key: 'aboutModal.highlight6' }
  ];

  return (
    <BaseModal 
      isOpen={isOpen} 
      onClose={onClose}
      maxWidth="max-w-4xl"
    >
      <div className="pt-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8 pr-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
            {t('aboutModal.title')}
          </h2>
          <p className="text-[#00d4ff] text-lg sm:text-xl font-semibold">
            {t('aboutModal.subtitle')}
          </p>
        </div>

        {/* Company Story */}
        <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
          <div className="glass rounded-2xl p-4 sm:p-6">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
              {t('aboutModal.storyTitle')}
            </h3>
            <div className="text-white/80 leading-relaxed space-y-3 sm:space-y-4 text-sm sm:text-base">
              <p>{t('aboutModal.storyParagraph1')}</p>
              <p>{t('aboutModal.storyParagraph2')}</p>
              <p>{t('aboutModal.storyParagraph3')}</p>
            </div>
          </div>

          {/* Mission & Vision */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="glass rounded-2xl p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">
                {t('aboutModal.missionTitle')}
              </h3>
              <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                {t('aboutModal.missionText')}
              </p>
            </div>
            <div className="glass rounded-2xl p-4 sm:p-6">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">
                {t('aboutModal.visionTitle')}
              </h3>
              <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                {t('aboutModal.visionText')}
              </p>
            </div>
          </div>

          {/* Key Highlights */}
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">
              {t('aboutModal.highlightsTitle')}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {highlights.map((item, index) => (
                <div 
                  key={index}
                  className="glass rounded-xl p-4 flex items-start space-x-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#00d4ff]/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="text-[#00d4ff] text-lg" />
                  </div>
                  <p className="text-white/80 text-sm flex-1">{t(item.key)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Certifications & Standards */}
          <div className="glass rounded-2xl p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">
              {t('aboutModal.certificationsTitle')}
            </h3>
            <div className="grid sm:grid-cols-2 gap-3 text-white/70 text-sm sm:text-base">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#00d4ff] rounded-full" />
                <span>{t('aboutModal.cert1')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#00d4ff] rounded-full" />
                <span>{t('aboutModal.cert2')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#00d4ff] rounded-full" />
                <span>{t('aboutModal.cert3')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-[#00d4ff] rounded-full" />
                <span>{t('aboutModal.cert4')}</span>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="glass-strong rounded-2xl p-6 sm:p-8 text-center bg-gradient-to-br from-[#00d4ff]/10 to-[#0099ff]/10">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
              {t('aboutModal.ctaTitle')}
            </h3>
            <p className="text-white/70 mb-4 sm:mb-6 text-sm sm:text-base">
              {t('aboutModal.ctaText')}
            </p>
            <button 
              onClick={() => {
                onClose();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary px-6 sm:px-8 py-3"
            >
              {t('aboutModal.ctaButton')}
            </button>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default AboutModal;
